import { useCallback, useState } from "react";
import { auth } from "../lib/firebase";
import { authenticatedFetch } from "../lib/api";
import { errorWithRef, fallbackError } from "../lib/apiError";

/**
 * ENG-2390 Phase 3 — the two sides of managed access.
 *
 * Both sides are loaded by one hook on purpose: they are the same relationship seen from opposite
 * ends, and an admin who is *both* an MSP and somebody else's client needs to see both at once
 * without the page having to reason about which it is.
 *
 * Design: `docs/MSP_ACCESS_MODEL.md`.
 */

/** A management relationship (mirror of the backend `ManagedTenantResponse`). */
export interface ManagedRelationship {
  id: string;
  mspTenantId: string;
  mspTenantName?: string | null;
  managedTenantId: string;
  managedTenantName?: string | null;
  /** Only `active` confers anything: `pending` awaits the client, `revoked` is terminal. */
  status: "pending" | "active" | "revoked";
  created: string;
  claimedAt?: string | null;
  revokedAt?: string | null;
  createdByEmail: string;
  /**
   * ENG-3347 — the provider's staff who can get into this organization, most senior first. Sent only
   * by `GET /business/managed-by`, where `[]` means nobody.
   *
   * ⚠ ABSENT means "not known", never "nobody": the provider side does not send it, and neither does a
   * backend older than this field (the console can deploy ahead of it). Rendering absence as "no one
   * has access" would be a false security claim, so every reader must check for `undefined` first.
   */
  staffWithAccess?: ProviderStaff[];
}

/** One of a provider's staff who can reach this organization (mirror of `ProviderStaffAccessResponse`). */
export interface ProviderStaff {
  email: string;
  /** Display name, already falling back to the email's local part server-side. */
  name: string;
  /** The most senior tier any of their groups holds here. */
  tier: MspTier;
}

/** A grant of access from one of our groups to one client (mirror of `MspAccessGrantResponse`). */
export interface MspGrant {
  id: string;
  tenantGroupId: string;
  tenantGroupName?: string | null;
  managedTenantId: string;
  managedTenantName?: string | null;
  tier: MspTier;
  created: string;
  grantedByEmail: string;
}

export type MspTier = "msp_admin" | "msp_policy" | "msp_read";

/**
 * ENG-3365 — the one way an Admin-tier provider can still add to what a client pays, said TO THE
 * CLIENT. For the customer-side Approve dialog only: "you" is the organization being managed, so on
 * any surface the provider's own admins read it would claim seats are added to THEIR subscription.
 * Everywhere else says it through the tier meaning below, in the third person.
 *
 * Billing is refused to every provider tier: the backend's `mayManageBilling` blocks the billing
 * portal and adding seats. Inviting is refused too, because `/tenant/invite` needs a membership in
 * the organization and a provider has none. But an Admin-tier provider can create a deployment token,
 * and each person it enrolls past the paid seats grows the subscription (`SeatService.ensureSeats`).
 */
export const PROVIDER_SEAT_GROWTH =
  "If they enroll more people than you have seats, for example with a deployment token, seats are added to your subscription.";

/** Appended to `PROVIDER_SEAT_GROWTH` only when the organization's seats are billed (`seatsBilled`). */
export const PROVIDER_SEAT_GROWTH_BILLED = "That increases what you pay.";

/**
 * What each tier actually permits, in the admin's words rather than the schema's.
 *
 * Kept beside the type so a new tier cannot be added to one without the other. `msp_policy` is the
 * one worth reading carefully: it is the tier that lets a provider do its job without being able to
 * touch who is in the organization.
 *
 * ⚠ Both sides read these: the client (on a provider's row, and in the Approve dialog's staff list)
 * and the provider's own admins (the Grant access caption, the Remove access confirmation, the tier
 * tooltip on a client). So a meaning never says "you" (ENG-3365).
 */
export const TIER_LABELS: Record<MspTier, { label: string; meaning: string }> = {
  msp_admin: {
    // ENG-3614: "Full admin" sat above a list of what the tier cannot do (invite, billing). It is an
    // admin's access from outside the organization, not everything an admin of it has.
    label: "Admin",
    meaning:
      "Can manage members, policy, groups and reporting, but can't invite people or manage billing. " +
      "People they enroll past the organization's seats, for example with a deployment token, add seats to its subscription.",
  },
  msp_policy: {
    label: "Policy only",
    meaning: "Can see everything and change protection settings, but cannot add, remove or re-role anyone.",
  },
  msp_read: {
    label: "Read only",
    meaning: "Can see everything, including reports. Cannot change anything.",
  },
};

/** Ordered most- to least-privileged, for pickers. */
export const TIER_ORDER: MspTier[] = ["msp_admin", "msp_policy", "msp_read"];

type Result = { success: boolean; error?: string };

async function mutate(path: string, method: string, body?: unknown): Promise<Result> {
  try {
    const response = await authenticatedFetch(path, {
      method,
      ...(body === undefined ? {} : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
    });
    if (response.ok) return { success: true };
    const data = await response.json().catch(() => ({}));
    return { success: false, error: errorWithRef(data, () => fallbackError("Request failed.", response)) };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : "Request failed." };
  }
}

export function useMspAccess() {
  const [clients, setClients] = useState<ManagedRelationship[]>([]);
  const [grants, setGrants] = useState<MspGrant[]>([]);
  const [managedBy, setManagedBy] = useState<ManagedRelationship[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load both sides.
   *
   * A failure sets `error` and leaves `loaded` FALSE, per ENG-2591/2595: the flag means "we have the
   * data", so marking a failed load as loaded would render a 403 as the "nobody manages you" empty
   * state — which here is worse than usual, since that empty state is a security claim.
   */
  const fetchAll = useCallback(async () => {
    if (!auth.currentUser) return;
    setLoading(true);
    try {
      const [clientsRes, grantsRes, managedByRes] = await Promise.all([
        authenticatedFetch("/business/msp/clients"),
        authenticatedFetch("/business/msp/grants"),
        authenticatedFetch("/business/managed-by"),
      ]);
      if (!clientsRes.ok || !grantsRes.ok || !managedByRes.ok) {
        const failed = [clientsRes, grantsRes, managedByRes].find((r) => !r.ok)!;
        const data = await failed.json().catch(() => ({}));
        setError(errorWithRef(data, () => fallbackError("Couldn't load managed access.", failed)));
        return;
      }
      const [clientsData, grantsData, managedByData] = await Promise.all([
        clientsRes.json(), grantsRes.json(), managedByRes.json(),
      ]);
      setClients(Array.isArray(clientsData) ? clientsData : []);
      setGrants(Array.isArray(grantsData) ? grantsData : []);
      setManagedBy(Array.isArray(managedByData) ? managedByData : []);
      setError(null);
      setLoaded(true);
    } catch (err) {
      console.error("Error fetching managed access:", err);
      setError(err instanceof Error ? err.message : "Couldn't load managed access.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Mutations RETURN their failure rather than writing `error`, which is the load-failure surface
  // only — the same split the other console hooks use, so a dialog's message isn't also printed in
  // the banner behind it.

  const claimClient = useCallback(async (managedTenantId: string): Promise<Result> => {
    const r = await mutate("/business/msp/clients", "POST", { managedTenantId: managedTenantId.trim() });
    if (r.success) await fetchAll();
    return r;
  }, [fetchAll]);

  const revokeClient = useCallback(async (relationshipId: string): Promise<Result> => {
    const r = await mutate(`/business/msp/clients/${encodeURIComponent(relationshipId)}/revoke`, "POST");
    if (r.success) await fetchAll();
    return r;
  }, [fetchAll]);

  const grantAccess = useCallback(async (
    tenantGroupId: string, managedTenantId: string, tier: MspTier,
  ): Promise<Result> => {
    const r = await mutate("/business/msp/grants", "POST", { tenantGroupId, managedTenantId, tier });
    if (r.success) await fetchAll();
    return r;
  }, [fetchAll]);

  const revokeGrant = useCallback(async (grantId: string): Promise<Result> => {
    const r = await mutate(`/business/msp/grants/${encodeURIComponent(grantId)}`, "DELETE");
    if (r.success) await fetchAll();
    return r;
  }, [fetchAll]);

  const acceptProvider = useCallback(async (relationshipId: string): Promise<Result> => {
    const r = await mutate(`/business/managed-by/${encodeURIComponent(relationshipId)}/accept`, "POST");
    if (r.success) await fetchAll();
    return r;
  }, [fetchAll]);

  const revokeProvider = useCallback(async (relationshipId: string): Promise<Result> => {
    const r = await mutate(`/business/managed-by/${encodeURIComponent(relationshipId)}/revoke`, "POST");
    if (r.success) await fetchAll();
    return r;
  }, [fetchAll]);

  const reset = useCallback(() => {
    setClients([]); setGrants([]); setManagedBy([]); setLoaded(false); setError(null);
  }, []);

  return {
    clients, grants, managedBy, loaded, loading, error,
    fetchAll, claimClient, revokeClient, grantAccess, revokeGrant, acceptProvider, revokeProvider, reset,
  };
}

/** The hook's public surface, so `DashboardLayout` can type the prop without re-declaring it. */
export type MspAccessApi = ReturnType<typeof useMspAccess>;
